import React, { FC, useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { Modal } from "components/modals/Modal"
import { Column, DataTable } from "components/tables/DataTable"

import { postbacksService } from "../../services/postbacks/postbacks.service"
import { LogFilters, POSTBACK_EVENTS, POSTBACK_STATUSES, PostbackEvent, PostbackLog, PostbackStatus } from "../../services/postbacks/type"

const PAGE_SIZE = 25

const statusClass: Record<PostbackStatus, string> = {
  SUCCESS: "border-0 bg-emerald-500/15 text-emerald-400",
  FAILED: "border-0 bg-rose-500/15 text-rose-400",
  PENDING: "border-0 bg-amber-500/15 text-amber-500",
  SKIPPED: "badge-ghost"
}

export const PostbackLogs: FC = () => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const [rows, setRows] = useState<PostbackLog[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [event, setEvent] = useState<PostbackEvent | "">("")
  const [status, setStatus] = useState<PostbackStatus | "">("")
  const [selected, setSelected] = useState<PostbackLog | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const filters: LogFilters = { page, pageSize: PAGE_SIZE }
    if (event) filters.event = event
    if (status) filters.status = status
    const result = await postbacksService.getLogs(filters)
    setRows(result.rows)
    setTotal(result.total)
    setLoading(false)
  }, [page, event, status])

  useEffect(() => {
    void load()
  }, [load])

  const replay = async (log: PostbackLog) => {
    if (await postbacksService.replay(log.id)) void load()
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const eventOptions = POSTBACK_EVENTS.map((ev) => ({ name: ev, value: ev }))
  const statusOptions = POSTBACK_STATUSES.map((s) => ({ name: s, value: s }))

  const columns: Column<PostbackLog>[] = [
    {
      header: t("s2sPostbacks.logs.time"),
      render: (log) => <span className="opacity-70">{new Date(log.created_at).toLocaleString()}</span>
    },
    {
      header: t("s2sPostbacks.logs.postback"),
      render: (log) => (
        <>
          {log.postback_name}
          {log.is_test && <span className="badge badge-sm badge-ghost ml-1">{t("s2sPostbacks.logs.test")}</span>}
        </>
      )
    },
    { header: t("s2sPostbacks.logs.event"), render: (log) => <code className="text-purple">{log.event}</code> },
    {
      header: t("s2sPostbacks.logs.status"),
      render: (log) => (
        <span className={`badge badge-sm ${statusClass[log.status]}`}>
          {log.status}
          {log.skip_reason ? `: ${log.skip_reason}` : ""}
        </span>
      )
    },
    { header: t("s2sPostbacks.logs.http"), render: (log) => log.http_status ?? "—" },
    { header: t("s2sPostbacks.logs.attempt"), render: (log) => log.attempt_number },
    {
      header: t("s2sPostbacks.logs.actions"),
      align: "right",
      render: (log) =>
        log.status === "FAILED" ? (
          <button
            className="btn btn-ghost btn-xs"
            onClick={(e) => {
              e.stopPropagation()
              void replay(log)
            }}
            type="button"
          >
            {t("s2sPostbacks.logs.replay")}
          </button>
        ) : null
    }
  ]

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t("s2sPostbacks.logs.title")}</h2>
        <div className="flex items-end gap-2">
          <div className="w-40">
            <DropdownMenu
              options={eventOptions}
              value={event}
              placeholder={t("s2sPostbacks.logs.allEvents")}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setPage(1)
                setEvent(e.target.value as PostbackEvent | "")
              }}
            />
          </div>
          <div className="w-40">
            <DropdownMenu
              options={statusOptions}
              value={status}
              placeholder={t("s2sPostbacks.logs.allStatuses")}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setPage(1)
                setStatus(e.target.value as PostbackStatus | "")
              }}
            />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => load()} type="button">
            {t("s2sPostbacks.logs.refresh")}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm opacity-60">{t("s2sPostbacks.logs.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm opacity-50">{t("s2sPostbacks.logs.empty")}</p>
      ) : (
        <DataTable rows={rows} columns={columns} rowKey={(log) => log.id} onRowClick={(log) => setSelected(log)} />
      )}

      <div className="mt-4 flex items-center justify-between text-sm opacity-70">
        <span>{t("s2sPostbacks.logs.entries", { count: total })}</span>
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost btn-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} type="button">
            {t("s2sPostbacks.logs.prev")}
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button className="btn btn-ghost btn-xs" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} type="button">
            {t("s2sPostbacks.logs.next")}
          </button>
        </div>
      </div>

      <Modal open={Boolean(selected)} handler={() => setSelected(null)}>
        {selected && (
          <div className="modal-box w-full max-w-xl">
            <h3 className="mb-3 text-lg font-semibold">
              {selected.postback_name} — <code className="text-purple">{selected.event}</code>
            </h3>
            <div className="space-y-2 text-xs">
              <div>
                <span className="opacity-60">{t("s2sPostbacks.logs.resolvedUrl")}</span>
                <div className="mt-1 break-all rounded bg-base-300 p-2 font-mono">{selected.resolved_url}</div>
              </div>
              <p>
                <span className="opacity-60">{t("s2sPostbacks.logs.subid")}</span>{" "}
                <span className="font-mono">{selected.subid || "—"}</span>
                {"   "}
                <span className="opacity-60">{t("s2sPostbacks.logs.tx")}</span>{" "}
                <span className="font-mono">{selected.transaction_id || "—"}</span>
              </p>
              {selected.error && <p className="text-rose-400">{t("s2sPostbacks.logs.error", { error: selected.error })}</p>}
              {selected.response_body && <pre className="max-h-40 overflow-auto rounded bg-base-300 p-2">{selected.response_body}</pre>}
            </div>
            <div className="modal-action">
              {selected.status === "FAILED" && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void replay(selected)
                    setSelected(null)
                  }}
                  type="button"
                >
                  {t("s2sPostbacks.logs.replay")}
                </button>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => setSelected(null)} type="button">
                {t("s2sPostbacks.test.close")}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
